import type { MultiSelectOption } from '@/components/common/MultiSelect';

/**
 * Declarative description of one filter control in a `ListFilterToolbar`
 * popover. Each variant carries the URL-search-param key(s) it reads/writes
 * so `FilterFieldsPanel` can render the control, the active-state pill, and
 * the "clear" action without the host page repeating that wiring per field.
 */
export type FilterFieldConfig =
  | {
      type: 'multiSelect';
      key: string;
      label: string;
      options: MultiSelectOption[];
      placeholder?: string;
    }
  | {
      type: 'dateRange';
      fromKey: string;
      toKey: string;
      label: string;
      placeholder?: string;
    }
  | {
      type: 'numberRange';
      minKey: string;
      maxKey: string;
      label: string;
    }
  | {
      type: 'toggleGroup';
      key: string;
      label: string;
      anyLabel: string;
      options: { value: string; label: string }[];
    }
  | {
      type: 'customer';
      key: string;
      label: string;
    };

/** URL-search-param bag a `FilterFieldConfig[]` reads from / patches into. */
export type FilterParams = Record<string, unknown>;

export function isFieldActive(
  field: FilterFieldConfig,
  params: FilterParams,
): boolean {
  switch (field.type) {
    case 'multiSelect':
    case 'customer': {
      const value = params[field.key];
      return Array.isArray(value) && value.length > 0;
    }
    case 'dateRange':
      return params[field.fromKey] != null || params[field.toKey] != null;
    case 'numberRange':
      return params[field.minKey] != null || params[field.maxKey] != null;
    case 'toggleGroup':
      return params[field.key] != null;
  }
}

export function getActiveFilterCount(
  fields: FilterFieldConfig[],
  params: FilterParams,
): number {
  return fields.filter((field) => isFieldActive(field, params)).length;
}

export function isAnyFieldActive(
  fields: FilterFieldConfig[],
  params: FilterParams,
): boolean {
  return fields.some((field) => isFieldActive(field, params));
}

export function buildFieldClearPatch(field: FilterFieldConfig): FilterParams {
  switch (field.type) {
    case 'multiSelect':
    case 'customer':
    case 'toggleGroup':
      return { [field.key]: undefined };
    case 'dateRange':
      return { [field.fromKey]: undefined, [field.toKey]: undefined };
    case 'numberRange':
      return { [field.minKey]: undefined, [field.maxKey]: undefined };
  }
}

export function buildClearPatch(fields: FilterFieldConfig[]): FilterParams {
  return fields.reduce<FilterParams>(
    (patch, field) => ({ ...patch, ...buildFieldClearPatch(field) }),
    {},
  );
}
